import { Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { FamilyMemberFormService } from 'src/app/_services/family-member-form.service';
import { SkippableInsuranceForm } from 'src/app/_services/interfaces/skippable-insurance-form';
import { ProposalService } from 'src/app/_services/proposal.service';
import { Offer, VehicleInsuranceOfferRequest } from 'src/app/_services/types/offers.types';
import { Lead, Proposal, VehicleMetadata } from 'src/app/_services/types/proposals.type';
import { UserAuthService } from 'src/app/_services/user-auth.service';
import { CentralisationInsurersEnum } from 'src/app/enums/centralisation-insurers.enum';
import { getSexEnumLabel } from 'src/app/enums/sex.enum';

import { User } from '../../_services/types/auth.types';
import { NPADetail } from '../../_services/types/npa.types';
import { CarMaker, CarModel, PolicyHolder } from '../../_services/types/vehicle.type';
import { InsuranceTypeEnum } from '../../enums/insurance-type.enum';
import {
  getVehicleDistanceLabel,
  VehicleDistanceEnum,
} from '../../enums/vehicle/vehicle-distance.enum';
import { getVehicleGarageLabel, VehicleGarageEnum } from '../../enums/vehicle/vehicle-garage.enum';
import { VehicleOfferTier } from '../../enums/vehicle/vehicle-offer-tier.enum';
import {
  getVehiclePrestationLabel,
  VehiclePrestationsEnum,
} from '../../enums/vehicle/vehicle-prestations.enum';
import {
  getVehicleRequestTypeLabel,
  VehicleRequestTypeEnum,
} from '../../enums/vehicle/vehicle-request-type.enum';
import { getVehicleUseLabel, VehicleUseEnum } from '../../enums/vehicle/vehicle-use.enum';

export const VEHICLE_INSURANCE_SERVICE_TOKEN = new InjectionToken<BaseVehicleService>(
  'BaseVehicleService',
);

export enum VehicleInputTypesEnum {
  VEHICLE = 'VEHICLE',
  CERTIFICATION = 'CERTIFICATION',
  LICENSE_PLATE = 'LICENSE_PLATE',
}

export class VehicleForm {
  type: InsuranceTypeEnum.MOTOR_VEHICLE = InsuranceTypeEnum.MOTOR_VEHICLE;

  requestType: VehicleRequestTypeEnum;

  vehicle: {
    certificateId?: string;
    immatriculation?: string;
    licensePlate?: string;
    brand: CarMaker;
    model: CarModel;
    firstImmatriculationDate: string;
  } = { brand: null, model: null, firstImmatriculationDate: null };

  distance: VehicleDistanceEnum;

  mainUse: VehicleUseEnum;

  parkingArea: string;

  garage: VehicleGarageEnum;

  isMainDriverDifferent: boolean;

  policyHolder: PolicyHolder<string>;

  leasing: boolean;

  leasingCompany?: string;

  tier: VehicleOfferTier = VehicleOfferTier.COMFORT;

  covers: {
    collisionDeductible?: number;
    partialCoverDeductible?: number;
    prestations?: VehiclePrestationsEnum[];
    personalBelongingsValue?: number;
  };

  insuranceStartDate: string;

  prestations: VehiclePrestationsEnum[];

  personalEffectsValue: number;

  format(scraping = false): VehicleInsuranceOfferRequest {
    return {
      type: this.type,
      requestType: this.requestType,
      vehicle: {
        certificateId: this.vehicle.certificateId,
        immatriculation: this.vehicle.immatriculation,
        licensePlate: this.vehicle.licensePlate,
        brand: this.vehicle.brand,
        model: this.vehicle.model,
        firstImmatriculationDate: new Date(
          this.vehicle.firstImmatriculationDate,
        ).toLocaleDateString('fr-FR'),
      },
      distance: this.distance,
      mainUse: this.mainUse,
      parkingArea: this.parkingArea,
      garage: this.garage,
      policyHolder: {
        birthDate: new Date(this.policyHolder.birthDate).toLocaleDateString('fr-FR'),
        licenseObtentionDate: this.policyHolder.licenseObtentionDate
          ? new Date(this.policyHolder.licenseObtentionDate).toLocaleDateString('fr-FR')
          : '',
        nationality: this.policyHolder.nationality,
        npa: this.policyHolder.npa,
        sex: this.policyHolder.sex,
        mainDriver: this.isMainDriverDifferent
          ? {
              birthDate: this.policyHolder.mainDriver.birthDate
                ? new Date(this.policyHolder.mainDriver.birthDate).toLocaleDateString('fr-FR')
                : '',
              licenseObtentionDate: this.policyHolder.mainDriver.licenseObtentionDate
                ? new Date(this.policyHolder.mainDriver.licenseObtentionDate).toLocaleDateString(
                    'fr-FR',
                  )
                : '',
              nationality: this.policyHolder.mainDriver.nationality,
              npa: this.policyHolder.mainDriver.npa,
              sex: this.policyHolder.mainDriver.sex,
            }
          : undefined,
      },
      leasing: this.leasing,
      leasingCompany: this.leasingCompany,
      covers: this.covers,
      insuranceStartDate: new Date(this.insuranceStartDate).toLocaleDateString('fr-FR'),
      scrapOffers: scraping,
    };
  }

  fromUser(user: User) {
    this.policyHolder = this.policyHolder ?? {
      birthDate: user.dob,
      nationality: user.nationality ?? user.country,
      npa: Number(user.postCode),
      sex: user.gender,
      licenseObtentionDate: null,
    };

    this.parkingArea = user.address ?? user.address;
  }

  originalLead?: Lead;

  originalLeadMetadata?: VehicleMetadata;

  originalLeadId?: string;

  originalProposalId?: string;

  fromLead(lead: Lead) {
    const metadata = lead.metadata as VehicleMetadata;
    this.originalLeadMetadata = metadata;
    this.originalLead = lead;
    this.originalLeadId = lead.leadId;
    if (metadata.requestType) this.requestType = metadata.requestType;
    if (metadata.vehicle) {
      this.vehicle = {
        brand: metadata.vehicle.brand,
        firstImmatriculationDate: metadata.vehicle.firstImmatriculationDate,
        model: metadata.vehicle.model,
        certificateId: metadata.vehicle.certificateId,
        immatriculation: metadata.vehicle.immatriculation,
        licensePlate: metadata.vehicle.licensePlate,
      };
    }
    if (metadata.distance) this.distance = metadata.distance;
    if (metadata.mainUse) this.mainUse = metadata.mainUse;
    if (metadata.parkingArea) this.parkingArea = metadata.parkingArea;
    if (metadata.garage) this.garage = metadata.garage;
    if (metadata.policyHolder) {
      this.policyHolder = {
        birthDate: metadata.policyHolder.birthDate,
        licenseObtentionDate: metadata.policyHolder.licenseObtentionDate,
        nationality: metadata.policyHolder.nationality,
        npa: metadata.policyHolder.npa,
        sex: metadata.policyHolder.sex,
      };
      if (metadata.policyHolder.mainDriver) {
        this.isMainDriverDifferent = true;
        this.policyHolder.mainDriver = {
          birthDate: metadata.policyHolder.mainDriver.birthDate,
          licenseObtentionDate: metadata.policyHolder.mainDriver.licenseObtentionDate,
          nationality: metadata.policyHolder.mainDriver.nationality,
          npa: metadata.policyHolder.mainDriver.npa,
          sex: metadata.policyHolder.mainDriver.sex,
        };
      }
    }
    if (metadata.leasing) this.leasing = metadata.leasing;
    if (metadata.leasingCompany) this.leasingCompany = metadata.leasingCompany;
    if (metadata.covers) this.covers = metadata.covers;
    if (metadata.insuranceStartDate) this.insuranceStartDate = metadata.insuranceStartDate;
    if (metadata.covers && metadata.covers.prestations)
      this.prestations = metadata.covers.prestations;
    if (metadata.covers && metadata.covers.personalBelongingsValue)
      this.personalEffectsValue = metadata.covers.personalBelongingsValue;
  }
}

@Injectable({
  providedIn: 'root',
})
export abstract class BaseVehicleService implements OnDestroy, SkippableInsuranceForm {
  private _insuranceFormModified = false;

  public npas: NPADetail[];

  get insuranceFormModified() {
    return this._insuranceFormModified;
  }

  set insuranceFormModified(value: boolean) {
    // If the form is already modified, the value will remain true--
    this._insuranceFormModified = this._insuranceFormModified || value;
  }

  resetFormModifiedState() {
    this._insuranceFormModified = false;
  }

  private _vehicleForm: VehicleForm = new VehicleForm();

  get vehicleForm() {
    return this._vehicleForm;
  }

  // Since the autoidentAPI doesn't return the catalog price when getting the list of models of a brand
  // we use this variable to store the details that we get separately from the API
  chosenVehicleDetails: CarModel;

  stepperObserver: BehaviorSubject<number[]> = new BehaviorSubject([1, 1]);

  inputType: VehicleInputTypesEnum;

  private _selectedOffer: Offer;

  get selectedOffer(): Offer {
    return this._selectedOffer;
  }

  set selectedOffer(value: Offer) {
    this._selectedOffer = value;
    if (this.payload?.metadata)
      (this.payload.metadata as VehicleMetadata).chosenBaseOffer = this.selectedOffer;
  }

  // Used to avoid fetching offers if the form was not modified
  offers: Offer[];

  scrapedOffers: Offer[];

  private _insurersToCompare: CentralisationInsurersEnum[];

  get insurersToCompare() {
    return this._insurersToCompare;
  }

  set insurersToCompare(value: CentralisationInsurersEnum[]) {
    this._insurersToCompare = value;
    if (this.payload.metadata)
      (this.payload.metadata as VehicleMetadata).insurersToCompare = this.insurersToCompare;
  }

  payload: Omit<Proposal, 'userId'>;

  navigationChangeSubscription: Subscription;

  constructor(
    protected readonly route: ActivatedRoute,
    protected readonly authService: UserAuthService,
    protected readonly proposalService: ProposalService,
    protected familyMemberService: FamilyMemberFormService,
  ) {}

  setVehicleBrand(brand: CarMaker) {
    this._vehicleForm.vehicle.brand = brand;
  }

  setVehicleModel(model: CarModel) {
    this._vehicleForm.vehicle.model = model;
  }

  setFirstImmatriculationDate(date: string) {
    this._vehicleForm.vehicle.firstImmatriculationDate = date;
  }

  setRequestType(requestType: VehicleRequestTypeEnum) {
    this._vehicleForm.requestType = requestType;
  }

  setImatriculation(immatriculation: string) {
    this._vehicleForm.vehicle.immatriculation = immatriculation;
  }

  setCertificateId(certification: string) {
    this._vehicleForm.vehicle.certificateId = certification;
  }

  setLicensePlate(licensePlate: string) {
    this._vehicleForm.vehicle.licensePlate = licensePlate;
  }

  setVehicleDistance(distance: VehicleDistanceEnum) {
    this._vehicleForm.distance = distance;
  }

  setvehicleMainUse(mainUse: VehicleUseEnum) {
    this._vehicleForm.mainUse = mainUse;
  }

  setLicenseObtentionDate(date: string) {
    this.setPolicyHolder({ licenseObtentionDate: date });
  }

  setParkingArea(parkingArea: string) {
    this._vehicleForm.parkingArea = parkingArea;
  }

  setGarage(garage: VehicleGarageEnum) {
    this._vehicleForm.garage = garage;
  }

  setPolicyHolder(policyHolder: Partial<PolicyHolder<string>>) {
    this._vehicleForm.policyHolder = { ...this._vehicleForm.policyHolder, ...policyHolder };
  }

  setIsMainDriverDifferent(isMainDriverDifferent: boolean) {
    this._vehicleForm.isMainDriverDifferent = isMainDriverDifferent;
  }

  setLeasing(leasing: boolean, leasingCompany?: string) {
    this._vehicleForm.leasing = leasing;
    if (!leasing) {
      this.vehicleForm.leasingCompany = null;
    } else {
      this._vehicleForm.leasingCompany = leasingCompany;
    }
  }

  setInsuranceStartDate(date: string) {
    this._vehicleForm.insuranceStartDate = date;
  }

  setPersonalEffectsValue(value: number) {
    this._vehicleForm.personalEffectsValue = value;
  }

  setPolicyHolderNPA(npa: number) {
    const policyHolder = { ...(this._vehicleForm.policyHolder ?? {}), npa } as PolicyHolder<string>;
    this.setPolicyHolder(policyHolder);
  }

  setCovers(covers: {
    prestations?: VehiclePrestationsEnum[];
    partialCoverDeductible?: number;
    collisionDeductible?: number;
    personalBelongingsValue?: number;
  }) {
    this._vehicleForm.covers = covers;
  }

  resetForm() {
    this._vehicleForm = new VehicleForm();
    this.selectedOffer = undefined;
    this.initDone = false;
    this.flowDone = true;
    this.familyMemberService.reset();
  }

  flowReady = new BehaviorSubject<boolean>(null);

  initDone = false;

  flowDone = false;

  protected handleQueryParams() {
    this.flowReady.next(false);
    if (this.route.snapshot.queryParamMap.get('leadId')) {
      const leadId: string = this.route.snapshot.queryParamMap.get('leadId');
      if (this.authService.isUserLoggedIn()) {
        this.proposalService.getLeadDetails(leadId).subscribe({
          next: (res) => {
            if (!res.data || !res.data.metadata) return;
            if (res.data.insuranceType !== InsuranceTypeEnum.MOTOR_VEHICLE)
              throw Error('Insurance type does not match the flow');
            if (res.data.metadata) {
              const vehicleMetadata = res.data.metadata as VehicleMetadata;
              this.setRequestType(vehicleMetadata.requestType);
            }
            this.vehicleForm.fromLead(res.data);
            this.flowReady.next(true);
          },
        });
      } else this.flowReady.next(true);
    } else if (this.route.snapshot.queryParamMap.get('proposalId')) {
      const proposalId: string = this.route.snapshot.queryParamMap.get('proposalId');
      if (this.authService.isUserLoggedIn()) {
        this.proposalService.getProposalDetails(proposalId).subscribe({
          next: (res) => {
            if (!res.data) return;
            if (res.data.insuranceType !== InsuranceTypeEnum.MOTOR_VEHICLE)
              throw Error('Insurance type does not match the flow');
            this.vehicleForm.originalProposalId = res.data.proposalId;
            // TODO: Handle the selected memberId if/once the flow is reworked to be able to choose the member
            this.fillFromUser();
          },
        });
      } else this.flowReady.next(true);
    } else if (this.authService.isUserLoggedIn()) {
      this.fillFromUser();
    } else this.flowReady.next(true);
    this.initDone = true;
  }

  ngOnDestroy(): void {
    this.navigationChangeSubscription.unsubscribe();
  }

  private fillFromUser() {
    this.authService.getuserByToken({ token: this.authService.getCurrentUser().token }).subscribe({
      next: (res) => {
        this.vehicleForm.fromUser(res.data);
        this.flowReady.next(true);
      },
    });
  }

  toString(): string {
    const form = this._vehicleForm;
    const parts: string[] = [];

    if (form.insuranceStartDate) {
      const startDate = new Date(form.insuranceStartDate);
      parts.push(`Date souhaitée de début d'assurance: ${startDate.toLocaleDateString('fr-FR')}`);
    }
    if (form.requestType) {
      parts.push(`S'agit-il de ...: ${getVehicleRequestTypeLabel(form.requestType)}`);
    }
    if (form.vehicle?.immatriculation) {
      parts.push(`Immatriculation: ${form.vehicle.immatriculation}`);
    }
    if (form.vehicle?.certificateId) {
      parts.push(`N° d'homologation: ${form.vehicle.certificateId}`);
    }
    if (form.vehicle?.licensePlate) {
      parts.push(`Plaque d'immatriculation: ${form.vehicle.licensePlate}`);
    }
    if (form.vehicle?.brand?.name) {
      parts.push(`Marque: ${form.vehicle.brand.name}`);
    }
    if (form.vehicle?.model?.name) {
      parts.push(`Modèle: ${form.vehicle.model.name}`);
    }
    if (form.vehicle?.firstImmatriculationDate) {
      const immatriculationDate = new Date(form.vehicle.firstImmatriculationDate);
      parts.push(
        `Date de la 1ère mise en circulation: ${immatriculationDate.toLocaleDateString('fr-FR')}`,
      );
    }
    if (form.distance) {
      parts.push(`Kilomètrage: ${getVehicleDistanceLabel(form.distance)}`);
    }
    if (form.leasing !== null && form.leasing !== undefined) {
      parts.push(`Le véhicule est-il en leasing ?: ${form.leasing ? 'Oui' : 'Non'}`);
    }
    if (form.leasing === true && form.leasingCompany) {
      parts.push(`Nom de la compagnie de leasing: ${form.leasingCompany}`);
    }
    if (form.mainUse) {
      parts.push(
        `Quel est l'utilisation principale du véhicule ?: ${getVehicleUseLabel(form.mainUse)}`,
      );
    }
    if (form.garage) {
      parts.push(
        `As-tu un garage de stationnement pour ton véhicule ?: ${getVehicleGarageLabel(form.garage)}`,
      );
    }
    if (form.parkingArea) {
      parts.push(`Lieu de stationnement: ${form.parkingArea}`);
    }
    if (form.policyHolder) {
      parts.push(`<br/>Informations sur le preneur d'assurance:<br/>`);
      if (form.policyHolder.sex) {
        parts.push(`Genre: ${getSexEnumLabel(form.policyHolder.sex)}`);
      }
      if (form.policyHolder.nationality) {
        parts.push(`Nationalité: ${form.policyHolder.nationality}`);
      }
      if (form.policyHolder.birthDate) {
        const birthDate = new Date(form.policyHolder.birthDate);
        parts.push(`Date de naissance: ${birthDate.toLocaleDateString('fr-FR')}`);
      }
      if (form.policyHolder.npa) {
        parts.push(`NPA: ${form.policyHolder.npa}`);
      }
      if (form.policyHolder.licenseObtentionDate) {
        const licenseDate = new Date(form.policyHolder.licenseObtentionDate);
        parts.push(
          `Date d'obtention du permis de conduire: ${licenseDate.toLocaleDateString('fr-FR')}`,
        );
      }
      if (form.isMainDriverDifferent !== null && form.isMainDriverDifferent !== undefined) {
        parts.push(
          `Le conducteur principal est-il différent du preneur d'assurance ?: ${form.isMainDriverDifferent ? 'Oui' : 'Non'}`,
        );
      }
      if (form.isMainDriverDifferent === true && form.policyHolder.mainDriver) {
        parts.push(`<br/>Conducteur principal:<br/>`);
        if (form.policyHolder.mainDriver.sex) {
          parts.push(
            `Genre du conducteur principal: ${getSexEnumLabel(form.policyHolder.mainDriver.sex)}`,
          );
        }
        if (form.policyHolder.mainDriver.nationality) {
          parts.push(
            `Nationalité du conducteur principal: ${form.policyHolder.mainDriver.nationality}`,
          );
        }
        if (form.policyHolder.mainDriver.npa) {
          parts.push(`NPA du conducteur principal: ${form.policyHolder.mainDriver.npa}`);
        }
        if (form.policyHolder.mainDriver.birthDate) {
          const mainDriverBirthDate = new Date(form.policyHolder.mainDriver.birthDate);
          parts.push(
            `Date de naissance du conducteur principal: ${mainDriverBirthDate.toLocaleDateString('fr-FR')}`,
          );
        }
      }
    }
    if (form.covers) {
      parts.push(`<br/>Franchises:<br/>`);
      if (
        form.covers.partialCoverDeductible !== null &&
        form.covers.partialCoverDeductible !== undefined
      ) {
        parts.push(`Casco partielle: ${form.covers.partialCoverDeductible} CHF`);
      }
      if (
        form.covers.collisionDeductible !== null &&
        form.covers.collisionDeductible !== undefined
      ) {
        parts.push(`Casco complète: ${form.covers.collisionDeductible} CHF`);
      }
    }
    if (form.prestations && form.prestations.length > 0) {
      parts.push(
        `<br/>Couvertures: ${form.prestations.map((p) => getVehiclePrestationLabel(p)).join(', ')}`,
      );
    }
    if (form.covers?.personalBelongingsValue) {
      parts.push(`Valeurs des effets personnels: ${form.covers.personalBelongingsValue} CHF`);
    }

    return parts.join('<br/>');
  }
}
